import TableBody from '@mui/material/TableBody'
import TablePagination from '@mui/material/TablePagination'
import {
  StyledPurchaseHistoryContainer,
  StyledTabContentTitle,
  StyledTable,
  StyledTableCell,
  StyledTableCellHead,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
} from './styled'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store/store'
import { useGetTransactionsQuery } from 'store/api-slices/userSlice'
import { Paper, TableCell, TableContainer, Typography } from '@mui/material'
import moment from 'moment'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import EmptyState from '../../empty-state/EmptyState'
import { Loader } from 'shared/components/loader/loader'
import useIsSafari from '../../../../../shared/hooks/useIsSafari'

export function PurchaseHistory() {
  const [page, setPage] = useState(0)
  const { t } = useTranslation('', {
    keyPrefix: 'pages.personalArea.tabs.purchaseHistory',
  })
  const direction = useSelector(
    (state: RootState) => state.layout.language.direction,
  )
  const isRTL = direction === 'rtl'

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }
  const rowsPerPage = 5
  const { data: transactions, isLoading, error } = useGetTransactionsQuery()
  const isMobile = useIsMobile()
  const isSafari = useIsSafari()

  if (isLoading) return <Loader isLoading={isLoading} />
  if (error && transactions) {
    return <Typography color="error">Error loading transactions.</Typography>
  }

  const isTransactionsEmpty =
    !transactions ||
    transactions.every(
      (transaction) =>
        transaction.seasonTickets.length === 0 &&
        transaction.tickets.length === 0,
    )

  if (isTransactionsEmpty) {
    return <EmptyState />
  }

  const rows = transactions
    ? transactions.flatMap((transaction) => {
        const date = transaction.paymentDate
          ? moment(new Date(transaction.paymentDate)).format('DD.MM.YYYY')
          : 'Unknown Date'
        return [
          ...transaction.seasonTickets.map((ticket) => ({
            orderNum: transaction.transactionId || 'Unknown',
            eventName: ticket.name || 'Unnamed Event',
            purchaseType: ticket.operation,
            date: date || 'Unknown Date',
            total: ticket.price ?? 0,
          })),
          ...transaction.tickets.map((ticket) => ({
            orderNum: transaction.transactionId || 'Unknown',
            eventName: ticket.name || 'Unnamed Event',
            purchaseType: ticket.operation,
            date: date || 'Unknown Date',
            total: ticket.price ?? 0,
          })),
        ]
      })
    : []

  const displayedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  )

  return (
    <StyledPurchaseHistoryContainer>
      <StyledTabContentTitle>{t('title')}</StyledTabContentTitle>
      <StyledTableContainer>
        <TableContainer
          component={Paper}
          style={{
            fontFamily: '"Inter", sans-serif',
            maxWidth: isMobile ? 400 : '100%',
            boxShadow: 'none',
            borderRadius: 4,
          }}
        >
          <StyledTable aria-label="simple table">
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCellHead
                  style={{
                    position: isMobile
                      ? isSafari
                        ? 'absolute'
                        : 'sticky'
                      : 'static',
                    [isRTL ? 'right' : 'left']: 0,
                    background: '#F3F3F3',
                    fontSize: '12px',
                    fontWeight: 600,
                    // height: '49px',
                    padding: '20px 20px 12px 20px',
                    textAlign: 'center',
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  {t('table.head.orderNum')}
                </StyledTableCellHead>
                <StyledTableCellHead>
                  {t('table.head.eventName')}
                </StyledTableCellHead>
                <StyledTableCellHead>
                  {t('table.head.purchaseType')}
                </StyledTableCellHead>
                <StyledTableCellHead>
                  {t('table.head.date')}
                </StyledTableCellHead>
                <StyledTableCellHead>
                  {t('table.head.total')}
                </StyledTableCellHead>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {displayedRows.map((row, index) => (
                <StyledTableRow key={index}>
                  <TableCell
                    style={{
                      position: isMobile
                        ? isSafari
                          ? 'absolute'
                          : 'sticky'
                        : 'static',
                      [isRTL ? 'right' : 'left']: 0,
                      background: '#FFFF',
                      fontSize: '12px',
                      fontWeight: 400,
                      height: '49px',
                      padding: '20px 20px 12px 20px',
                      color: '#2e2e2e',
                      textAlign: 'center',
                      fontFamily: '"Inter", sans-serif',
                    }}
                    component="th"
                    scope="row"
                  >
                    {row.orderNum}
                  </TableCell>
                  <StyledTableCell>{row.eventName}</StyledTableCell>
                  <StyledTableCell>
                    {row.purchaseType === 'Sale'
                      ? t('table.actionsName.sale')
                      : t('table.actionsName.return')}
                  </StyledTableCell>
                  <StyledTableCell>{row.date}</StyledTableCell>
                  <StyledTableCell>{row.total.toFixed(2)}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </StyledTable>
        </TableContainer>
        <TablePagination
          sx={{
            '.css-16og44m-MuiToolbar-root-MuiTablePagination-toolbar': {
              display: 'flex',
              flexDirection: isRTL ? 'row-reverse' : 'row',
              padding: '20px 20px 12px 20px',
            },
            '.css-levciy-MuiTablePagination-displayedRows': {
              fontSize: '12px ',
              fontWeight: 500,
              fontFamily: '"Inter", sans-serif',
            },
          }}
          rowsPerPageOptions={[5]}
          component="section"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          nextIconButtonProps={{
            style: { transform: isRTL ? 'scaleX(-1)' : '' },
          }}
          backIconButtonProps={{
            style: { transform: isRTL ? 'scaleX(-1)' : '' },
          }}
        />
      </StyledTableContainer>
    </StyledPurchaseHistoryContainer>
  )
}
